import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, doc, updateDoc, Timestamp } from 'firebase/firestore';
import { useAuth } from '../../../hooks/useAuth';
import { useEmailVerification } from '../../../hooks/useEmailVerification';
import { db, storage } from '../../../firebase';
import { ref, uploadBytes, deleteObject } from 'firebase/storage';
import ClientLayout from '../../../components/layout/ClientLayout';
import { PortvilleResidency } from '../../../types/portvilleRoom';

type ExtensionDocumentType = 'identification' | 'employment' | 'other';
type ExtensionDocuments = Partial<Record<ExtensionDocumentType, string>>;
type ExtensionRound = 1 | 2;

type ResidencyWithSecondExtension = PortvilleResidency & {
  hasSecondExtended?: boolean;
  secondExtensionStatus?: string;
  secondExtensionAppliedAt?: Date;
  secondExtensionApprovedAt?: Date;
  secondExtensionContractSignedAt?: Date;
  secondOriginalEndDate?: Date;
};

const getRequestedRound = (search: string): ExtensionRound | null => {
  const roundParam = new URLSearchParams(search).get('round');
  if (roundParam === '1') return 1;
  if (roundParam === '2') return 2;
  return null;
};

const getDefaultRoundFromResidencyData = (residencyData: Record<string, any>): ExtensionRound | null => {
  const hasExtended = Boolean(residencyData.hasExtended);
  const hasSecondExtended = Boolean(residencyData.hasSecondExtended);
  if (!hasExtended) return 1;
  if (hasExtended && !hasSecondExtended) return 2;
  return null;
};

const getRoundLabel = (round: ExtensionRound) => (round === 1 ? '1차' : '2차');

const EXTENSION_APPLICATION_WINDOW_DAYS = 60;

const getDocumentDisplayName = (docType: ExtensionDocumentType) => {
  switch (docType) {
    case 'identification':
      return '신분증 사본';
    case 'employment':
      return '재직증명서/사업자등록증';
    case 'other':
      return '기타 서류';
  }
};

const getOriginalFileNameFromStoragePath = (storagePath: string) => {
  const fileName = storagePath.split('/').pop() || storagePath;
  const extracted = fileName.split('_').slice(3).join('_');
  return extracted || fileName;
};

const ContractExtensionPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();
  const { loading: emailVerificationLoading } = useEmailVerification();
  const [loading, setLoading] = useState(true);
  const [residencyInfo, setResidencyInfo] = useState<ResidencyWithSecondExtension | null>(null);
  const [extensionRound, setExtensionRound] = useState<ExtensionRound | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [extensionDocuments, setExtensionDocuments] = useState<ExtensionDocuments>({});
  const [uploadingDocType, setUploadingDocType] = useState<ExtensionDocumentType | null>(null);
  const [documentError, setDocumentError] = useState<string | null>(null);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/signin');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user && !authLoading && !emailVerificationLoading) {
      loadResidencyInfo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading, emailVerificationLoading, location.search]);

	  const loadResidencyInfo = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // portville_residencies 컬렉션에서 현재 사용자의 active 계약 조회
      const residenciesRef = collection(db, 'portville_residencies');
      const residencyQuery = query(
        residenciesRef, 
        where('residentUid', '==', user.uid),
        where('status', '==', 'active')
      );
      const residencySnapshot = await getDocs(residencyQuery);
      
      if (residencySnapshot.empty) {
        setError('현재 활성화된 계약 정보를 찾을 수 없습니다.');
        return;
      }
      
      if (residencySnapshot.docs.length > 1) {
        console.warn('Multiple active contracts found for user:', user.uid);
      }
      
	      // 첫 번째 (또는 유일한) 활성 계약 사용
	      const residencyDoc = residencySnapshot.docs[0];
	      const residencyData = residencyDoc.data();
        const requestedRound = getRequestedRound(location.search);
        const inferredRound = getDefaultRoundFromResidencyData(residencyData);
        const effectiveRound = requestedRound ?? inferredRound;

        if (effectiveRound === null) {
          setError('이미 계약 연장(최대 2회)이 완료되어 추가 연장 신청이 불가합니다.');
          return;
        }
        if (effectiveRound === 2 && !residencyData.hasExtended) {
          setError('2차 연장 신청은 1차 연장 완료 후 가능합니다.');
          return;
        }

        setExtensionRound(effectiveRound);
        const documentsField =
          effectiveRound === 2 ? residencyData.secondExtensionDocuments : residencyData.extensionDocuments;
        setExtensionDocuments((documentsField || {}) as ExtensionDocuments);
	      
	      console.log('=== ContractExtensionPage 계약 정보 ===');
	      console.log('residencyData:', residencyData);
	      console.log('extensionStatus:', residencyData.extensionStatus);
	      console.log('hasExtended:', residencyData.hasExtended);
      console.log('=======================================');
      
      const residency: ResidencyWithSecondExtension = {
        id: residencyDoc.id,
        roomId: residencyData.roomId,
        roomNumber: residencyData.roomNumber,
        residentUid: residencyData.residentUid,
        residentInfo: residencyData.residentInfo,
        status: residencyData.status,
        occupancyPeriod: residencyData.occupancyPeriod ? {
          startDate: residencyData.occupancyPeriod.startDate?.toDate() || new Date(),
          endDate: residencyData.occupancyPeriod.endDate?.toDate() || new Date()
        } : undefined,
        roommateInfo: residencyData.roommateInfo,
        vehicleInfo: residencyData.vehicleInfo,
        notes: residencyData.notes,
        signatureName: residencyData.signatureName,
        signatureDate: residencyData.signatureDate?.toDate(),
        signatureUrl: residencyData.signatureUrl,
        contractSignedAt: residencyData.contractSignedAt?.toDate(),
        moveInInspectionSignedAt: residencyData.moveInInspectionSignedAt?.toDate(),
        moveInInspectionSignatureUrl: residencyData.moveInInspectionSignatureUrl,
        landlordSignedContractUrl: residencyData.landlordSignedContractUrl,
        landlordSignedContractUploadedAt: residencyData.landlordSignedContractUploadedAt?.toDate(),
        contractContent: residencyData.contractContent,
        hasExtended: residencyData.hasExtended || false,
        extensionStatus: residencyData.extensionStatus || 'none',
        extensionAppliedAt: residencyData.extensionAppliedAt?.toDate(),
        extensionApprovedAt: residencyData.extensionApprovedAt?.toDate(),
        originalEndDate: residencyData.originalEndDate?.toDate(),
        hasSecondExtended: residencyData.hasSecondExtended || false,
        secondExtensionStatus: residencyData.secondExtensionStatus || 'none',
        secondExtensionAppliedAt: residencyData.secondExtensionAppliedAt?.toDate(),
        secondExtensionApprovedAt: residencyData.secondExtensionApprovedAt?.toDate(),
        secondExtensionContractSignedAt: residencyData.secondExtensionContractSignedAt?.toDate(),
        secondOriginalEndDate: residencyData.secondOriginalEndDate?.toDate(),
        createdAt: residencyData.createdAt?.toDate() || new Date(),
        updatedAt: residencyData.updatedAt?.toDate() || new Date(),
        completedAt: residencyData.completedAt?.toDate()
      };
      
      setResidencyInfo(residency);
      
    } catch (error) {
      console.error('Error loading residency info:', error);
      setError('계약 정보를 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
	  };

  const validateUploadFile = (file: File): string | null => {
    const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
    if (file.size > MAX_FILE_SIZE) {
      return `파일 크기는 5MB를 초과할 수 없습니다. (현재: ${(file.size / 1024 / 1024).toFixed(2)}MB)`;
    }

    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      return `허용되지 않는 파일 형식입니다. PDF, JPG, PNG 파일만 업로드할 수 있습니다. (현재: ${file.type})`;
    }

    const fileName = file.name.toLowerCase();
    const allowedExtensions = ['.pdf', '.jpg', '.jpeg', '.png'];
    const hasValidExtension = allowedExtensions.some((ext) => fileName.endsWith(ext));
    if (!hasValidExtension) {
      return '허용되지 않는 파일 확장자입니다. .pdf, .jpg, .jpeg, .png 파일만 업로드할 수 있습니다.';
    }

    return null;
  };

  const uploadExtensionDocument = async (type: ExtensionDocumentType, file: File) => {
    if (!user || !residencyInfo || !extensionRound) return;

    const status = extensionRound === 2 ? residencyInfo.secondExtensionStatus : residencyInfo.extensionStatus;
    const hasCompletedThisRound =
      extensionRound === 2 ? Boolean(residencyInfo.hasSecondExtended) : Boolean(residencyInfo.hasExtended);

    const isLocked =
      hasCompletedThisRound || (status === 'approved' || status === 'contracted');
    if (isLocked) {
      setDocumentError('연장 신청이 승인/완료된 이후에는 서류를 추가 업로드/변경할 수 없습니다.');
      return;
    }

    const validationError = validateUploadFile(file);
    if (validationError) {
      setDocumentError(validationError);
      return;
    }

    const existingPath = extensionDocuments?.[type];
    try {
      setUploadingDocType(type);
      setDocumentError(null);

      const timestamp = Date.now();
      const safeOriginalName = file.name.split('/').join('_');
      const fileName = `${user.uid}_${type}_${timestamp}_${safeOriginalName}`;
      const storageBasePath =
        extensionRound === 2 ? 'portville_second_extension_documents' : 'portville_extension_documents';
      const storagePath = `${storageBasePath}/${user.uid}/${residencyInfo.id}/${fileName}`;
      const storageRef = ref(storage, storagePath);

      try {
        // 1) 새 파일 업로드
        await uploadBytes(storageRef, file, { contentType: file.type });

        // 2) Firestore에 새 경로 반영 (성공해야만 이전 파일을 지움)
        const updatedDocuments: ExtensionDocuments = {
          ...(extensionDocuments || {}),
          [type]: storagePath
        };

        const documentsKey = extensionRound === 2 ? 'secondExtensionDocuments' : 'extensionDocuments';
        const documentsUpdatedAtKey =
          extensionRound === 2 ? 'secondExtensionDocumentsUpdatedAt' : 'extensionDocumentsUpdatedAt';
        await updateDoc(doc(db, 'portville_residencies', residencyInfo.id), {
          [documentsKey]: updatedDocuments,
          [documentsUpdatedAtKey]: Timestamp.now(),
          updatedAt: Timestamp.now()
        });

        setExtensionDocuments(updatedDocuments);

        // 3) 기존 파일 정리 (best effort)
        if (existingPath && existingPath !== storagePath) {
          try {
            await deleteObject(ref(storage, existingPath));
          } catch (deleteError) {
            console.warn('기존 파일 삭제 실패:', deleteError);
            setDocumentError('파일은 업로드되었지만, 기존 파일 삭제에 실패했습니다. 관리자에게 문의해주세요.');
          }
        }
      } catch (innerError) {
        // Firestore 반영 실패/업로드 실패 시 새로 업로드된 파일 정리 (best effort)
        try {
          await deleteObject(storageRef);
        } catch (cleanupError) {
          console.warn('업로드 실패 후 새 파일 정리 실패:', cleanupError);
        }
        throw innerError;
      }
    } catch (uploadError) {
      console.error('서류 업로드 실패:', uploadError);
      setDocumentError('서류 업로드 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.');
    } finally {
      setUploadingDocType(null);
    }
  };

  const deleteOptionalExtensionDocument = async () => {
    if (!user || !residencyInfo || !extensionRound) return;

    const status = extensionRound === 2 ? residencyInfo.secondExtensionStatus : residencyInfo.extensionStatus;
    const hasCompletedThisRound =
      extensionRound === 2 ? Boolean(residencyInfo.hasSecondExtended) : Boolean(residencyInfo.hasExtended);

    const isLocked =
      hasCompletedThisRound || (status === 'approved' || status === 'contracted');
    if (isLocked) {
      setDocumentError('연장 신청이 승인/완료된 이후에는 서류를 삭제할 수 없습니다.');
      return;
    }

    const existingPath = extensionDocuments?.other;
    if (!existingPath) return;

    try {
      setUploadingDocType('other');
      setDocumentError(null);

      const updatedDocuments: ExtensionDocuments = { ...(extensionDocuments || {}) };
      delete updatedDocuments.other;

      const documentsKey = extensionRound === 2 ? 'secondExtensionDocuments' : 'extensionDocuments';
      const documentsUpdatedAtKey =
        extensionRound === 2 ? 'secondExtensionDocumentsUpdatedAt' : 'extensionDocumentsUpdatedAt';
      await updateDoc(doc(db, 'portville_residencies', residencyInfo.id), {
        [documentsKey]: updatedDocuments,
        [documentsUpdatedAtKey]: Timestamp.now(),
        updatedAt: Timestamp.now()
      });

      setExtensionDocuments(updatedDocuments);

      // Firestore 반영 후 Storage 파일 삭제 (best effort)
      try {
        await deleteObject(ref(storage, existingPath));
      } catch (deleteError) {
        console.warn('기존 파일 삭제 실패:', deleteError);
        setDocumentError('서류는 목록에서 제거되었지만, Storage 파일 삭제에 실패했습니다. 관리자에게 문의해주세요.');
      }
    } catch (deleteError) {
      console.error('서류 삭제 실패:', deleteError);
      setDocumentError('서류 삭제 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.');
    } finally {
      setUploadingDocType(null);
    }
  };

  const handleExtensionApplication = async () => {
    if (!user || !residencyInfo || !extensionRound) return;
    
    try {
      const documents = extensionDocuments || {};
      const hasRequiredDocuments = Boolean(documents.identification && documents.employment);
      if (!hasRequiredDocuments) {
        setDocumentError('필수 서류(신분증 사본, 재직증명서/사업자등록증)를 업로드한 후 연장 신청이 가능합니다.');
        return;
      }

      setSubmitting(true);
      
      // 계약 연장 신청 처리
      const residencyRef = doc(db, 'portville_residencies', residencyInfo.id);
      const statusKey = extensionRound === 2 ? 'secondExtensionStatus' : 'extensionStatus';
      const appliedAtKey = extensionRound === 2 ? 'secondExtensionAppliedAt' : 'extensionAppliedAt';
      const originalEndDateKey = extensionRound === 2 ? 'secondOriginalEndDate' : 'originalEndDate';
      const originalEndDateAlreadySet =
        extensionRound === 2 ? Boolean(residencyInfo.secondOriginalEndDate) : Boolean(residencyInfo.originalEndDate);

      const payload: Record<string, any> = {
        [statusKey]: 'applied',
        [appliedAtKey]: Timestamp.now(),
        updatedAt: Timestamp.now()
      };
      if (!originalEndDateAlreadySet && residencyInfo.occupancyPeriod?.endDate) {
        payload[originalEndDateKey] = Timestamp.fromDate(residencyInfo.occupancyPeriod.endDate);
      }

      await updateDoc(residencyRef, payload);
      
      // 상태 업데이트
      setResidencyInfo((prev) => {
        if (!prev) return null;
        const next: Record<string, any> = {
          ...prev,
          updatedAt: new Date()
        };
        next[statusKey] = 'applied';
        next[appliedAtKey] = new Date();
        if (!originalEndDateAlreadySet && prev.occupancyPeriod?.endDate) {
          next[originalEndDateKey] = prev.occupancyPeriod.endDate;
        }
        return next as ResidencyWithSecondExtension;
      });
      
      alert('계약 연장 신청이 완료되었습니다.\n신항시설관리센터에서 검토 후 연락드리겠습니다.');
      
    } catch (error) {
      console.error('Error submitting extension application:', error);
      alert('계약 연장 신청 중 오류가 발생했습니다. 다시 시도해주세요.');
    } finally {
      setSubmitting(false);
    }
  };

  if (authLoading || emailVerificationLoading) {
    return (
      <ClientLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      </ClientLayout>
    );
  }

  if (!user) {
    return null;
  }

  if (loading) {
    return (
      <ClientLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      </ClientLayout>
    );
  }

  if (error) {
    return (
      <ClientLayout>
        <div className="flex flex-col min-h-screen bg-white">
          <div className="flex-1 flex justify-center py-8 sm:py-12 md:py-[115px]">
            <div className="w-full max-w-[750px] px-4 sm:px-6">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-8">
                <p className="text-red-800 text-center">{error}</p>
              </div>
              <button
                onClick={() => navigate('/portville/residency')}
                className="w-full h-14 sm:h-16 bg-gray-100 text-gray-700 rounded-sm hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors text-base sm:text-lg font-bold font-['Pretendard'] leading-9"
              >
                입주 현황으로 돌아가기
              </button>
            </div>
          </div>
        </div>
      </ClientLayout>
    );
  }

  if (!residencyInfo) {
    return null;
  }

  if (!extensionRound) {
    return (
      <ClientLayout>
        <div className="flex flex-col min-h-screen bg-white">
          <div className="flex-1 flex justify-center py-8 sm:py-12 md:py-[115px]">
            <div className="w-full max-w-[750px] px-4 sm:px-6">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-8">
                <p className="text-red-800 text-center">연장 신청 round 정보를 확인할 수 없습니다.</p>
              </div>
              <button
                onClick={() => navigate('/portville/residency')}
                className="w-full h-14 sm:h-16 bg-gray-100 text-gray-700 rounded-sm hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors text-base sm:text-lg font-bold font-['Pretendard'] leading-9"
              >
                입주 현황으로 돌아가기
              </button>
            </div>
          </div>
        </div>
      </ClientLayout>
    );
  }

  // 계약 종료일까지 남은 일수 계산
  const getDaysUntilExpiry = () => {
    if (!residencyInfo.occupancyPeriod) return null;
    const today = new Date();
    const expiryDate = residencyInfo.occupancyPeriod.endDate;
    const diffTime = expiryDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const daysUntilExpiry = getDaysUntilExpiry();
  const roundLabel = getRoundLabel(extensionRound);
  const currentStatus = extensionRound === 2 ? residencyInfo.secondExtensionStatus : residencyInfo.extensionStatus;
  const currentAppliedAt =
    extensionRound === 2 ? residencyInfo.secondExtensionAppliedAt : residencyInfo.extensionAppliedAt;
  const currentApprovedAt =
    extensionRound === 2 ? residencyInfo.secondExtensionApprovedAt : residencyInfo.extensionApprovedAt;
  const currentContractSignedAt =
    extensionRound === 2 ? residencyInfo.secondExtensionContractSignedAt : residencyInfo.extensionContractSignedAt;
  const currentHasCompletedThisRound =
    extensionRound === 2 ? Boolean(residencyInfo.hasSecondExtended) : Boolean(residencyInfo.hasExtended);

  const canApplyExtension = (
    currentStatus === 'none' || currentStatus === 'rejected'
  ) && !currentHasCompletedThisRound && daysUntilExpiry !== null && daysUntilExpiry <= EXTENSION_APPLICATION_WINDOW_DAYS && daysUntilExpiry >= 1;

  const isDocumentUploadLocked =
    currentHasCompletedThisRound || (currentStatus === 'approved' || currentStatus === 'contracted');
  const hasRequiredDocuments = Boolean(extensionDocuments.identification && extensionDocuments.employment);

  return (
    <ClientLayout>
      <div className="flex flex-col min-h-screen bg-white">
        <div className="flex-1 flex justify-center py-8 sm:py-12 md:py-[115px]">
          <div className="w-full max-w-[750px] px-4 sm:px-6">
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-slate-800 font-['Pretendard'] leading-9 mb-4">
              포트빌 {roundLabel} 계약 연장 신청
            </h1>
            <p className="text-base sm:text-lg md:text-xl font-normal text-slate-700 font-['Pretendard'] leading-9 mb-8">
              계약 연장을 위한 정보를 입력해주세요
            </p>
            <div className="w-full h-px bg-gray-200 mb-8"></div>

            {/* 현재 계약 정보 */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
              <div className="flex items-center mb-4">
                <svg className="w-6 h-6 text-blue-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <h3 className="text-lg font-bold text-blue-900">현재 계약 정보</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <dt className="text-sm font-medium text-blue-600">호실번호</dt>
                  <dd className="text-base font-semibold text-blue-900">{residencyInfo.roomNumber}호</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-blue-600">입주자</dt>
                  <dd className="text-base font-semibold text-blue-900">{residencyInfo.residentInfo.name}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-blue-600">계약 시작일</dt>
                  <dd className="text-base font-semibold text-blue-900">
                    {residencyInfo.occupancyPeriod?.startDate.toLocaleDateString('ko-KR')}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-blue-600">계약 종료일</dt>
                  <dd className="text-base font-semibold text-blue-900">
                    {residencyInfo.occupancyPeriod?.endDate.toLocaleDateString('ko-KR')}
                  </dd>
                </div>
              </div>
              {daysUntilExpiry !== null && (
                <div className="mt-4 p-3 bg-white border border-blue-200 rounded">
                  <p className="text-sm text-blue-800">
                    📅 계약 종료까지 <span className="font-bold">{daysUntilExpiry}일</span> 남았습니다.
                  </p>
                </div>
              )}
            </div>

            {/* 연장 상태별 UI */}
            {(() => {
              switch (currentStatus) {
                case 'applied':
                  return (
                    <>
                      <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-8">
                        <div className="flex items-center mb-4">
                          <svg className="w-6 h-6 text-green-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <h3 className="text-lg font-bold text-green-900">{roundLabel} 계약 연장 신청 완료</h3>
                        </div>
                        <div className="text-green-800 space-y-2">
                          <p>• 계약 연장 신청이 완료되었습니다.</p>
                          <p>• 신청일: {currentAppliedAt?.toLocaleDateString('ko-KR')}</p>
                          <p>• 신항시설관리센터에서 검토 후 연락드리겠습니다.</p>
                          <p className="text-sm text-green-700">
                            • 승인/거절 처리 전까지는 첨부파일을 변경할 수 있습니다.
                          </p>
                        </div>
                      </div>

                      {/* 연장 신청 첨부 서류 (승인/거절 전까지 교체 가능) */}
                      <div className="bg-white border border-gray-200 rounded-lg p-6 mb-8">
                        <h3 className="text-lg font-bold text-gray-900 mb-2">연장 신청 첨부 서류</h3>
                        <p className="text-sm text-gray-600 mb-4">
                          제출한 서류가 최신인지 확인해주세요. (승인/거절 전까지 수정 가능)
                        </p>

                        {documentError && (
                          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
                            {documentError}
                          </div>
                        )}
                        {isDocumentUploadLocked && (
                          <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded text-yellow-900 text-sm">
                            연장 신청이 승인/완료되어 서류를 추가 업로드/변경할 수 없습니다.
                          </div>
                        )}

                        <div className="space-y-4">
                          {(['identification', 'employment', 'other'] as const).map((type) => {
                            const storagePath = extensionDocuments[type];
                            const isRequired = type === 'identification' || type === 'employment';

                            return (
                              <div key={type} className="flex flex-col gap-2">
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                                  <span className="text-slate-800 font-medium font-['Pretendard']">
                                    {getDocumentDisplayName(type)}
                                    {isRequired && <span className="ml-2 text-xs text-red-500">(필수)</span>}
                                  </span>
                                  <div className="flex gap-2 flex-wrap sm:flex-nowrap">
                                    <input
                                      type="file"
                                      onChange={(e) => {
                                        const file = e.target.files?.[0];
                                        e.target.value = '';
                                        if (file) uploadExtensionDocument(type, file);
                                      }}
                                      className="hidden"
                                      id={`extension-file-${extensionRound}-applied-${type}`}
                                      accept=".pdf,.jpg,.jpeg,.png"
                                      disabled={uploadingDocType !== null || isDocumentUploadLocked}
                                    />
                                    <label
                                      htmlFor={`extension-file-${extensionRound}-applied-${type}`}
                                      className={`px-3 md:px-4 py-1.5 md:py-2 rounded transition-colors cursor-pointer text-sm md:text-base whitespace-nowrap ${
                                        uploadingDocType !== null || isDocumentUploadLocked
                                          ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                      }`}
                                    >
                                      {uploadingDocType === type
                                        ? '업로드 중...'
                                        : isDocumentUploadLocked
                                          ? '업로드 불가'
                                          : storagePath
                                            ? '파일 변경'
                                            : '파일 선택'}
                                    </label>
                                    {type === 'other' && storagePath && (
                                      <button
                                        onClick={deleteOptionalExtensionDocument}
                                        disabled={uploadingDocType !== null || isDocumentUploadLocked}
                                        className={`px-3 md:px-4 py-1.5 md:py-2 rounded transition-colors text-sm md:text-base whitespace-nowrap ${
                                          uploadingDocType !== null || isDocumentUploadLocked
                                            ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                            : 'bg-red-100 text-red-700 hover:bg-red-200'
                                        }`}
                                      >
                                        삭제
                                      </button>
                                    )}
                                  </div>
                                </div>

                                {storagePath ? (
                                  <div className="p-2 bg-green-50 border border-green-200 rounded text-sm text-green-800">
                                    업로드 완료: {getOriginalFileNameFromStoragePath(storagePath)}
                                  </div>
                                ) : (
                                  <div className="text-sm text-slate-500">아직 업로드되지 않았습니다.</div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </>
                  );
                
                case 'approved':
                  return (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-8">
                      <div className="flex items-center mb-4">
                        <svg className="w-6 h-6 text-green-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <h3 className="text-lg font-bold text-green-900">{roundLabel} 계약 연장 승인</h3>
                      </div>
                      <div className="text-green-800 space-y-2">
                        <p>• 계약 연장이 승인되었습니다.</p>
                        <p>• 승인일: {currentApprovedAt?.toLocaleDateString('ko-KR')}</p>
                        <p>• 새로운 계약서 작성을 위해 신항시설관리센터에 연락주세요.</p>
                      </div>
                    </div>
                  );
                  
                case 'contracted':
                  return (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
                      <div className="flex items-center mb-4">
                        <svg className="w-6 h-6 text-blue-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <h3 className="text-lg font-bold text-blue-900">{roundLabel} 계약 연장 완료</h3>
                      </div>
                      <div className="text-blue-800 space-y-2">
                        <p>• 계약 연장이 완료되었습니다.</p>
                        {currentContractSignedAt && (
                          <p>• 연장 계약 날인일: {currentContractSignedAt.toLocaleDateString('ko-KR')}</p>
                        )}
                        <p>• 새로운 계약 종료일: {(() => {
                          if (!residencyInfo.occupancyPeriod) return '';
                          return residencyInfo.occupancyPeriod.endDate.toLocaleDateString('ko-KR');
                        })()}</p>
                        <p>• 연장된 계약이 적용되었습니다.</p>
                      </div>
                    </div>
                  );
                
                case 'rejected':
                  return (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-8">
                      <div className="flex items-center mb-4">
                        <svg className="w-6 h-6 text-red-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <h3 className="text-lg font-bold text-red-900">계약 연장 거절</h3>
                      </div>
                      <div className="text-red-800 space-y-2">
                        <p>• 계약 연장 신청이 거절되었습니다.</p>
                        <p>• 기존 계약 종료일에 퇴거해야 합니다.</p>
                        <p>• 자세한 사항은 신항시설관리센터에 문의해주세요.</p>
                      </div>
                    </div>
                  );
                
                default:
                  if (!canApplyExtension) {
                    if (currentHasCompletedThisRound) {
                      return (
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-8">
                          <div className="flex items-center mb-4">
                            <svg className="w-6 h-6 text-gray-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <h3 className="text-lg font-bold text-gray-700">계약 연장 불가</h3>
                          </div>
                          <div className="text-gray-600 space-y-2">
                            <p>• 계약 연장은 최대 2회까지 가능합니다.</p>
                            <p>• 이미 {roundLabel} 계약 연장을 사용하셨습니다.</p>
                          </div>
                        </div>
                      );
                    } else if (daysUntilExpiry !== null && daysUntilExpiry > EXTENSION_APPLICATION_WINDOW_DAYS) {
                      return (
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-8">
                          <div className="flex items-center mb-4">
                            <svg className="w-6 h-6 text-gray-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <h3 className="text-lg font-bold text-gray-700">연장 신청 기간 아님</h3>
                          </div>
                          <div className="text-gray-600 space-y-2">
                            <p>• 계약 연장은 계약 종료 {EXTENSION_APPLICATION_WINDOW_DAYS}일 전부터 신청 가능합니다.</p>
                            <p>• 현재 계약 종료까지 {daysUntilExpiry}일 남았습니다.</p>
                          </div>
                        </div>
                      );
                    }
                  }
                  
                  return (
                    <>
                      {/* 안내 메시지 */}
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
                        <div className="flex items-center mb-4">
                          <svg className="w-6 h-6 text-blue-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <h3 className="text-lg font-bold text-blue-900">계약 연장 신청 안내</h3>
                        </div>
                        <div className="text-blue-800 space-y-2">
                          <p>• 계약 연장은 최대 2회까지 신청 가능합니다.</p>
                          <p>• 연장 기간은 기존 계약 기간과 동일하게 2년입니다.</p>
                          <p>• 신청 후 신항시설관리센터에서 검토하여 승인 여부를 결정합니다.</p>
                          <p>• 연장 승인 시 새로운 계약서 작성이 필요합니다.</p>
                          <p>• 이번 신청은 {roundLabel} 연장입니다.</p>
                        </div>
                      </div>

                      {/* 주의사항 */}
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-8">
                        <div className="flex items-center mb-4">
                          <svg className="w-6 h-6 text-yellow-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                          </svg>
                          <h3 className="text-lg font-bold text-yellow-900">주의사항</h3>
                        </div>
                        <div className="text-yellow-800 space-y-2">
                          <p>• 계약 연장은 승인 후 취소가 불가능합니다.</p>
                          <p>• 연장 거부 시 기존 계약 종료일에 퇴거해야 합니다.</p>
                          <p>• 연장 신청 기간은 계약 종료 {EXTENSION_APPLICATION_WINDOW_DAYS}일 전부터 가능합니다.</p>
                        </div>
                      </div>

	                      {/* 계약 연장 신청 버튼 */}
	                      <div className="bg-white border border-gray-200 rounded-lg p-6 mb-8">
	                        <h3 className="text-lg font-bold text-gray-900 mb-4">계약 연장 신청</h3>
	                                                 <p className="text-gray-600 mb-6">
	                           현재 계약을 2년 연장하시겠습니까?<br />
	                           연장 승인 시 새로운 계약 종료일은 <span className="font-bold">
                             {(() => {
                               if (!residencyInfo.occupancyPeriod) return '';
                               const newEndDate = new Date(residencyInfo.occupancyPeriod.endDate);
                               newEndDate.setDate(newEndDate.getDate() + 730); // 2년 = 729일
                               return newEndDate.toLocaleDateString('ko-KR');
                             })()}
                           </span>입니다.
	                         </p>

                        {/* 연장 신청 전 서류 업로드 */}
                        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <h4 className="font-bold text-blue-900 mb-1">연장 신청 전 서류 업로드</h4>
                          <p className="text-sm text-blue-800">
                            연장 신청 접수 전, 필수 서류 업로드가 필요합니다.
                          </p>
                        </div>

                        <div className="mt-4 bg-white rounded-lg border border-slate-200 p-4">
                          {documentError && (
                            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
                              {documentError}
                            </div>
                          )}
                          {isDocumentUploadLocked && (
                            <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded text-yellow-900 text-sm">
                              연장 신청 처리 상태이므로 서류를 추가 업로드/변경할 수 없습니다.
                            </div>
                          )}

                          <div className="space-y-4">
                            {(['identification', 'employment', 'other'] as const).map((type) => {
                              const storagePath = extensionDocuments[type];
                              const isRequired = type === 'identification' || type === 'employment';

                              return (
                                <div key={type} className="flex flex-col gap-2">
                                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                                    <span className="text-slate-800 font-medium font-['Pretendard']">
                                      {getDocumentDisplayName(type)}
                                      {isRequired && <span className="ml-2 text-xs text-red-500">(필수)</span>}
                                    </span>
                                    <div className="flex gap-2 flex-wrap sm:flex-nowrap">
	                                      <input
	                                        type="file"
	                                        onChange={(e) => {
	                                          const file = e.target.files?.[0];
	                                          e.target.value = '';
	                                          if (file) uploadExtensionDocument(type, file);
	                                        }}
	                                        className="hidden"
	                                        id={`extension-file-${extensionRound}-ready-${type}`}
	                                        accept=".pdf,.jpg,.jpeg,.png"
	                                        disabled={uploadingDocType !== null || isDocumentUploadLocked}
	                                      />
	                                      <label
	                                        htmlFor={`extension-file-${extensionRound}-ready-${type}`}
	                                        className={`px-3 md:px-4 py-1.5 md:py-2 rounded transition-colors cursor-pointer text-sm md:text-base whitespace-nowrap ${
	                                          uploadingDocType !== null || isDocumentUploadLocked
	                                            ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
	                                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
	                                        }`}
	                                      >
                                        {uploadingDocType === type
                                          ? '업로드 중...'
                                          : isDocumentUploadLocked
                                            ? '업로드 불가'
                                            : storagePath
                                              ? '파일 변경'
                                              : '파일 선택'}
                                      </label>
                                      {type === 'other' && storagePath && (
                                        <button
                                          onClick={deleteOptionalExtensionDocument}
                                          disabled={uploadingDocType !== null || isDocumentUploadLocked}
                                          className={`px-3 md:px-4 py-1.5 md:py-2 rounded transition-colors text-sm md:text-base whitespace-nowrap ${
                                            uploadingDocType !== null || isDocumentUploadLocked
                                              ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                              : 'bg-red-100 text-red-700 hover:bg-red-200'
                                          }`}
                                        >
                                          삭제
                                        </button>
                                      )}
                                    </div>
                                  </div>

                                  {storagePath ? (
                                    <div className="p-2 bg-green-50 border border-green-200 rounded text-sm text-green-800">
                                      업로드 완료: {getOriginalFileNameFromStoragePath(storagePath)}
                                    </div>
                                  ) : (
                                    <div className="text-sm text-slate-500">아직 업로드되지 않았습니다.</div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>

	                        <button
	                          onClick={handleExtensionApplication}
	                          disabled={submitting || uploadingDocType !== null || !hasRequiredDocuments}
	                          className="w-full h-14 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors font-bold disabled:opacity-50 disabled:cursor-not-allowed"
	                        >
	                          {submitting ? (
	                            <div className="flex items-center justify-center">
	                              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
	                              신청 처리중...
                            </div>
	                          ) : (
	                            '계약 연장 신청하기'
	                          )}
	                        </button>
                          {!hasRequiredDocuments && (
                            <p className="mt-3 text-sm text-slate-600 text-center font-['Pretendard']">
                              필수 서류 업로드 후 연장 신청이 가능합니다.
                            </p>
                          )}
	                      </div>
	                    </>
	                  );
	              }
            })()}

            {/* 연락처 정보 */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-8">
              <h4 className="font-bold text-gray-800 mb-4">문의처</h4>
              <div className="text-gray-600">
                <p className="font-semibold">부산항신항시설관리센터</p>
                <p>전화: 051-890-7465</p>
                <p>운영시간: 평일 08:00 ~ 17:00</p>
              </div>
            </div>

            {/* 버튼 영역 */}
            <div className="mt-8 space-y-4">
              <button
                onClick={() => navigate('/portville/residency')}
                className="w-full h-14 sm:h-16 bg-blue-600 text-white rounded-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors text-base sm:text-lg font-bold font-['Pretendard'] leading-9"
              >
                입주 현황으로 돌아가기
              </button>
              
              <button
                onClick={() => navigate('/portville')}
                className="w-full h-14 sm:h-16 bg-gray-100 text-gray-700 rounded-sm hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors text-base sm:text-lg font-bold font-['Pretendard'] leading-9"
              >
                포트빌 메인으로 돌아가기
              </button>
            </div>
          </div>
        </div>
      </div>
    </ClientLayout>
  );
};

export default ContractExtensionPage; 
